/**
 * CheckList
 * - window.device
 * 
 */
import axiosOrigin from 'axios';
import qs from 'qs';
import { getTradingViewTimezone, wait, isLoginedToken } from './utils';
import Cookies from 'js-cookie';
import * as Sentry from '@sentry/vue';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
// import { NuxtApp } from 'nuxt/app';
import { useIndexStore } from '~/store';
import { setAppLoginInfo } from '~/common/js/storeUtil';
import { refreshToken, isTimeOut, isGlobalRenewing, setRenewing } from '~/frontend-shared/helper/refresh-token';
import { COOKIE_NAME } from '~/frontend-shared/helper/login-util';

export interface IResponse<T> {
  success: boolean;
  msg?: string;
  code?: number;
  data: T
}


const clientInstance = axiosOrigin.create({
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
  },
  transformRequest(data, headers) {
    const appToken = Cookies.get(COOKIE_NAME.APP_TOKEN);
    if (
      appToken &&
      appToken !== 'undefined' && isAppRuntime
    ) {
      headers.Authorization = appToken;
    } else {
      // 不要删除 Authorization，有可能是调用的地方手动传
      // delete headers.Authorization
    }
    /**  老的Token如果有则兼容赋值到 headers.Authorization === START ===稳定后删除 */
    const oldToken = Cookies.get(COOKIE_NAME.TOKEN);
    if (
      oldToken &&
      oldToken !== 'undefined' && !isAppRuntime
    ) {
      headers.Authorization = oldToken;
    }
    /**  老的Token如果有则兼容赋值到 headers.Authorization === END ===稳定后删除 */
    if (headers.getContentType()?.includes('application/json')) {
      if (data?.timestamp) {
        headers['X-BM-TIMESTAMP'] = data.timestamp;
        delete data.timestamp;
      }
      if (data?.sign) {
        headers['X-BM-SIGN'] = data.sign;
        delete data.sign;
      }
      return JSON.stringify(data);
    } else if (headers.getContentType()?.includes('multipart/form-data')) {
      return data;
    } else {
      return qs.stringify(data);
    }
  },
  timeout: 15 * 1000,
});

// const setLoginInfo = async (data: any, ctx: NuxtApp) => {
//   const store = useIndexStore();
//   store.SET_USER(data.loginName);
//   store.SET_TOKEN(data.accessToken);
//   store.SET_SALT(data.accessSalt);
// }

const clearLoginInfo = async () => {
  const store = useIndexStore();
  store.SET_CLEAN_INFO();
};

export function setAxiosHeaders(options: Record<string, string | number>) {
  // console.log('setAxiosHeaders===', options);
  Object.keys(options).forEach((name) => {
    clientInstance.defaults.headers[`X-BM-${name}`] = options[name];
  });
}

const bitMartAppPattern = /BitMart/;
const ERROR_TIME_OUT = 'TIME_OUT';
const retryMaxTime = 5;
let isAppRuntime = false;

const getUserSession = async (current: number) => {
  if (current >= retryMaxTime) {
    return 0;
  }
  if (bitMartAppPattern.test(navigator.userAgent)) {
    const diff = 100;
    let totalTime = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (window.jsBridge && window.jsBridge.getUserSession) {
        break;
      } else {
        if (totalTime >= 1000) {
          break;
        }
        await wait(diff);
        totalTime += diff;
      }
    }
  }
  if (window.jsBridge?.getUserSession) {
    const userPromise = new Promise((resolve) => {
      window.jsBridge.getUserSession((resp: string) => {
        const appUserData = resp.replace(/(Optional\("(.*)"\)\))/, '$2');
        // 在app 中
        console.log('===userPromise====resolve===', current);
        // 在BM APP 里面执行
        const res = String(appUserData).replace(/(Optional\("(.*)"\)\))/, '$2');
        const data = JSON.parse(res);
        const matchedResult = (data.userAgent || '').match(/BitMart\/(\S*) \(/);
        const version = matchedResult && Array.isArray(matchedResult) ? matchedResult[1] : '';
        const store = useIndexStore();
        setAxiosHeaders({
          CLIENT: data.client,
          DEVICE: data.deviceID,
          UA: data.userAgent,
          VERSION: version,
          TIMEZONE: getTradingViewTimezone(),
          'TIMEZONE-OFFSET': new Date().getTimezoneOffset(),
          CONTRACT: 2,
          LOCAL: store.normalizedLocale,
        });
        // 初始的一次
        Sentry.setContext('AppInfo', {
          deviceID: data.deviceID,
          client: data.client,
          userAgent: data.userAgent,
          version,
        });
        if (data.success) {
          setAppLoginInfo(null, data);
        } else {
          clearLoginInfo();
        }
        resolve(1);
      });
    });
    const defaultPromise = new Promise((resolve) => {
      if (clientInstance.defaults.headers?.['X-BM-CLIENT']) {
        console.log('===defaultPromise====resolve===', current);
        resolve(2);
      }
    });
    const timeOutPromise = new Promise((resolve) => {
      setTimeout(() => {
        console.log('===timeOutPromise====resolve===', current);
        resolve(ERROR_TIME_OUT);
      }, 200);
    });
    // 去做递归
    return new Promise((resolve) => {
      Promise.race([userPromise, defaultPromise, timeOutPromise]).then((value) => {
        if (value === ERROR_TIME_OUT) {
          current++;
          resolve(getUserSession(current));
        } else {
          resolve(value);
        }
      });
    });
  }
};

export const initAxiosHeaders = async () => {
  if (process.client) {
    const hasDelegation = Cookies.get('hasDelegation') || false;
    const delegationType = Cookies.get('delegationType') || 0;
    const agency = hasDelegation ? delegationType || 0 : 0;
    isAppRuntime = bitMartAppPattern.test(navigator?.userAgent);
    const status = await getUserSession(0);
    if (window.jsBridge?.getUserSession && (status === 1 || status === 2)) {
      return clientInstance.defaults.headers;
    } else {
      // 浏览器执行
      const store = useIndexStore();
      setAxiosHeaders({
        CLIENT: 'WEB',
        UA: navigator.userAgent,
        VERSION: process.env.version || '1.0.0',
        TIMEZONE: getTradingViewTimezone(),
        'TIMEZONE-OFFSET': new Date().getTimezoneOffset(),
        LOCAL: store.normalizedLocale,
        // 写死2代
        CONTRACT: 2,
        AGENCY: String(agency),
      });
      FingerprintJS.load()
        .then((fp) => fp.get())
        .then((result) => {
          window.device = result.visitorId;
          setAxiosHeaders({
            DEVICE: result.visitorId,
          });
        })
        .catch(() => {
          setAxiosHeaders({
            DEVICE: localStorage.getItem('getAnonymousID') || '1',
          });
        });
    }
  }
  return clientInstance.defaults.headers;
};

export const axiosClient = clientInstance;

// 是否正在刷新token
let isRenewIng = false;

axiosClient.interceptors.request.use(async (req) => {
  if (!req.headers?.['X-BM-CLIENT']) {
    const headers: any = await initAxiosHeaders();
    req.headers = {
      ...headers,
      ...req.headers,
    };
  }
  if (!isAppRuntime && isLoginedToken() && isTimeOut()) {
    // 在非app中运行 && 当前是登录状态 && 距离过期时间不到12小时
    let retryCount = 0;
    while (isGlobalRenewing() && retryCount < 20) {
      if (retryCount === 0) {
        // 第一次 ===> 说明在别的页面进行了renew操作
        Sentry.captureMessage('token_renewing_repeat', {
          level: 'warning',
          tags: {
            retryCount,
            v: 'next'
          },
        });
      } else if (retryCount >= 20) {
        // 兜底 ===> 轮询20次renew还没响应 则主动设置renewing为false 跳出循环
        setRenewing(false);
        Sentry.captureMessage('token_renewing_repeat', {
          level: 'warning',
          tags: {
            retryCount,
            v: 'next'
          },
        });
      }
      await wait(500);
      retryCount++;
    }
  }
  // 已登录，且距离过期时间不到12小时
  if (!isAppRuntime && isLoginedToken() && isTimeOut() && !isRenewIng) {
    isRenewIng = true;
    refreshToken(req).finally(() => {
      isRenewIng = false;
    });
  };
  
  return req;
});

/**
 * 用于nuxt发起的请求，非客户端。
 */
export const axiosNuxt = axiosOrigin.create({
  baseURL: import.meta.env.VITE_NUXT_GATEWAY_URL,
  headers: {
    'X-BM-CLIENT': 'WEB',
  },
  transformRequest: (data, headers) => {
    if (headers.getContentType()?.includes('application/json')) {
      return JSON.stringify(data);
    }
    return qs.stringify(data);
  },
  timeout: 10 * 1000,
});

axiosNuxt.interceptors.response.use(
  (res) => {
    if (res.status === 200) {
      return res.data;
    }
    return null;
  },
  (err) => {
    const { response } = err;
    return response;
  }
);
